import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

import {
  DownOutlined as ExpandMore,
  UpOutlined as ExpandLess,
  UsergroupAddOutlined as GroupAdd,
  SearchOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Row, Col, Select, Switch, Tooltip, Input, Button, Avatar } from 'antd';

import CustomMaskedInput from 'components/MaskedInput/index';
import type ICoProducer from 'interfaces/ICoProducer';
import customerService from 'v2/services/cutomer';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import Validations from './validations';
import { PartnerStatus } from '../Status';

interface IProps {
  coproducer: ICoProducer;
  onChangeRequiredFields: (...args: any[]) => any;
  onSearchSuccess: (...args: any[]) => any;
  index: number;
  disabled?: boolean;
  isProducer: boolean;
  onChangeField: (...args: any[]) => any;
  onRemove: (...args: any[]) => any;
  pendingStatus?: string;
  coproducers: Array<ICoProducer>;
}

function CoProducerForm(props: IProps) {
  const {
    coproducer,
    index,
    disabled,
    isProducer,
    onChangeField,
    onRemove,
    onChangeRequiredFields,
    onSearchSuccess,
    coproducers
  } = props;

  const { user } = useUserStore(({ user }) => ({ user }));

  const [validationErrors, setValidationErrors] = useState<any>({});
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [existUser, setExistUser] = useState<any>('');
  const [permissions, setPermissions] = useState(false);

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const onTooglePermissions = useCallback(() => {
    setPermissions(prev => !prev);
  }, []);

  const runValidation = useCallback(() => {
    const newValidationErrors = run(coproducer, Validations());
    setValidationErrors(newValidationErrors);

    if (onChangeRequiredFields) {
      onChangeRequiredFields(
        'coProducerRequiredFields',
        newValidationErrors ? Object.keys(newValidationErrors).length : 0
      );
    }
  }, [coproducer, onChangeRequiredFields]);

  useEffect(() => {
    runValidation();
  }, [runValidation]);

  const onChange = useCallback(
    (field: any, fieldIndex: any, value: any) => {
      if (onChangeField) {
        onChangeField(field, fieldIndex, value);
      }
    },
    [onChangeField]
  );

  const onSearchCustomerByEmail = useCallback(
    (email: any) => {
      const alreadyHas = coproducers.filter(c => c.email === email && !c.pending).length > 1;
      setExistUser('validating');

      const executeSearch = async () => {
        if (!email) {
          onChange('email', index, '');
          onChange('business_name', index, '');
          return;
        }

        if (alreadyHas || email === user.email) {
          toast.error(`O e-mail ${email} não pode ser cadastrado novamente pois já faz parte dos parceiros`);
          setIsValidEmail(false);
          onChange('email', index, '');
          onChange('business_name', index, '');
          return;
        }

        const customerData = await customerService.loadCustomerByEmail(email);

        if (!customerData.length) {
          runValidation();
          return;
        }

        await onSearchSuccess(index, customerData[0]);
        setIsValidEmail(true);
      };

      executeSearch();
    },
    [coproducers, user, index, onChange, onSearchSuccess, runValidation]
  );

  const isAffiliatesSelectedAndNFNotSelected = coproducer.only_over_affiliates && !coproducer.fiscal_document;
  const isNFSelectedAndAffiliatesNotSelected = !coproducer.only_over_affiliates && coproducer.fiscal_document;

  const handleRemoveClick = () => {
    if (!isProducer) {
      onRemove(index);
    }
  };

  const handleKeyPress = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter') {
      onSearchCustomerByEmail(coproducer.email);
      ev.preventDefault();
    }
  };

  const emailHelperText = isValid('email')
    ? errorFor('email')
    : isValidEmail
      ? null
      : 'Insira um e-mail que não é um parceiro já cadastrado';
  const emailHasError = isValid('email') || !isValidEmail;

  return (
    <div className='rounded-lg border border-[#E0E0E0] bg-[#EEEEEE]'>
      <div className='flex items-center gap-3 p-4'>
        <Avatar style={{ backgroundColor: '#087922' }} icon={<GroupAdd />} />
        <div className='min-w-0 flex-1'>
          <div className='text-base font-medium'>Novo parceiro</div>
        </div>
        <Tooltip title='Remover parceiro' placement='top'>
          <Button
            type='text'
            icon={<DeleteOutlined className='hover:text-[#dc3f53]' style={{ fontSize: 24 }} />}
            onClick={handleRemoveClick}
          />
        </Tooltip>
      </div>

      <div className='px-4 pb-4'>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <label className='mb-1 block text-sm'>E-mail do parceiro</label>
            <Input
              id={`partner-email-${index}`}
              disabled={disabled || isProducer || !coproducer.new}
              onChange={e => onChangeField('email', index, e.target.value)}
              value={coproducer.email}
              onBlur={() => onSearchCustomerByEmail(coproducer.email)}
              onKeyPress={handleKeyPress}
              status={emailHasError ? 'error' : undefined}
              suffix={
                <Button
                  type='text'
                  size='small'
                  onClick={() => onSearchCustomerByEmail(coproducer.email)}
                  disabled={disabled || isProducer || !coproducer.email || !coproducer.new}
                  icon={<SearchOutlined />}
                />
              }
            />
            {emailHelperText && (
              <p className={`mt-1 text-xs ${emailHasError ? 'text-red-500' : 'text-gray-500'}`}>{emailHelperText}</p>
            )}
          </Col>

          <Col span={24}>
            <label className='mb-1 block text-sm'>Nome do parceiro</label>
            <Input
              id={`partner-name-${index}`}
              disabled={true}
              status={existUser === 'error' ? 'error' : undefined}
              value={coproducer.business_name}
            />
            {existUser === 'error' && <p className='mt-1 text-xs text-red-500'>Parceiro não encontrado</p>}
          </Col>

          <Col span={24}>
            <label className='mb-1 block text-sm'>Tipo de comissão</label>
            <Select
              className='w-full'
              disabled={disabled || coproducer.comission_type === 'P'}
              value={coproducer.comission_type}
              onChange={value => onChange('comission_type', index, value)}
              options={[
                { value: 'P', label: 'Porcentagem' },
                ...(coproducer.comission_type === 'V' ? [{ value: 'V', label: 'Valor', disabled: true }] : [])
              ]}
            />
          </Col>

          <Col span={24}>
            {coproducer.comission_type === 'P' && (
              <CustomMaskedInput
                disabled={disabled}
                id={`partner-commission-value-${index}`}
                mask='percent'
                noStrictMaxPercent
                label='Valor'
                helperText={isValid('comission_value') ? errorFor('comission_value') : null}
                error={isValid('comission_value')}
                startAdornment='%'
                fullWidth={true}
                disableUnderline={true}
                onChange={(value: any) => onChange('comission_value', index, value)}
                value={String(coproducer.comission_value)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            )}

            {coproducer.comission_type === 'V' && (
              <CustomMaskedInput
                disabled={disabled}
                id={`partner-commission-value-${index}`}
                mask='money'
                label='Valor'
                helperText={isValid('comission_value') ? errorFor('comission_value') : null}
                error={isValid('comission_value')}
                startAdornment='R$'
                disableUnderline={true}
                fullWidth={true}
                onChange={(value: any) => onChange('comission_value', index, value)}
                value={String(coproducer.comission_value)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className='flex items-center justify-between px-4 pb-2'>
        <PartnerStatus cop={coproducer} />
        <Button size='small' onClick={onTooglePermissions}>
          Ver permissões {permissions ? <ExpandLess /> : <ExpandMore />}
        </Button>
      </div>

      {permissions && (
        <div className='px-4 pb-4'>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer || isNFSelectedAndAffiliatesNotSelected}
                  checked={coproducer.only_over_affiliates}
                  onChange={checked => onChangeField('only_over_affiliates', index, checked)}
                />
                <span>Afiliados</span>
              </div>
              <p className='mt-1! mb-0! text-xs text-gray-500'>
                O coprodutor será comissionado somente sobre vendas realizadas através de afiliados.{' '}
                <a
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807551969-Como-adicionar-um-Co-Produtor-%C3%93rbita-'
                  target='blank'
                >
                  Saiba mais
                </a>
                {isNFSelectedAndAffiliatesNotSelected && (
                  <p className='mt-1! mb-0! text-xs text-red-500'>
                    Não é possível ter &quot;Afiliados&quot; e &quot;Emitir NF&quot; marcados ao mesmo tempo.
                  </p>
                )}
              </p>
            </Col>
            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer}
                  checked={coproducer.affiliate_manager}
                  onChange={checked => onChangeField('affiliate_manager', index, checked)}
                />
                <span>Gerir afiliados</span>
              </div>
              <p className='mt-1! mb-0! text-xs text-gray-500'>
                O coprodutor consegue: aprovar/ reprovar afiliações, ver relatório de afiliados e conversar com
                afiliados de todas as variações.
              </p>
            </Col>

            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer || isAffiliatesSelectedAndNFNotSelected}
                  checked={coproducer.fiscal_document}
                  onChange={checked => onChangeField('fiscal_document', index, checked)}
                />
                <span>Emitir NF</span>
              </div>
              <p className='mt-1! mb-0! text-xs text-gray-500'>
                O coprodutor também será responsável por emitir Nota Fiscal aos clientes.{' '}
                <a
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/4402651955739-Como-Funciona-a-Central-de-Documentos-Fiscais-%C3%93rbita-'
                  target='blank'
                >
                  Saiba Mais
                </a>
                {isAffiliatesSelectedAndNFNotSelected && (
                  <p className='mt-1! mb-0! text-xs text-red-500'>
                    Não é possível ter &quot;Afiliados&quot; e &quot;Emitir NF&quot; marcados ao mesmo tempo.
                  </p>
                )}
              </p>
            </Col>

            <Col span={24}>
              <div className='flex items-center gap-2'>
                <Switch
                  disabled={disabled || isProducer}
                  checked={coproducer.permission_refund}
                  onChange={checked => onChangeField('permission_refund', index, checked)}
                />
                <span>Reembolsar</span>
              </div>
              <p className='mt-1! mb-0! text-xs text-gray-500'>
                Quando habilitado, permite o coprodutor reembolsar faturas.
              </p>
            </Col>

            {!isProducer && (
              <Col span={24}>
                <div className='flex items-center gap-2'>
                  <Switch
                    disabled={disabled || isProducer}
                    checked={coproducer.visibility_customer_email}
                    onChange={checked => onChangeField('visibility_customer_email', index, checked)}
                  />
                  <span>Ver cliente</span>
                </div>
                <p className='mt-1! mb-0! text-xs text-gray-500'>
                  O coprodutor poderá visualizar os dados dos clientes nos relatórios.
                </p>
              </Col>
            )}
          </Row>
        </div>
      )}
    </div>
  );
}

export default CoProducerForm;
